import { motion } from "framer-motion";
import { MessageCircle } from "lucide-react";

const WA_MESSAGE = encodeURIComponent("PaintKing chat incoming");
const WA_URL = `https://wa.me/15616984044?text=${WA_MESSAGE}`;

/**
 * On-site section inviting visitors to text Paul on WhatsApp.
 * Sits between Praise and Coverage (or wherever chosen in App.js).
 */
export function FancyTexters() {
    return (
        <section
            id="whatsapp"
            data-testid="fancy-texters"
            className="relative bg-black py-24 md:py-40 overflow-hidden border-t border-white/10"
        >
            {/* Ambient rainbow glow */}
            <div className="absolute inset-0 pointer-events-none">
                <div
                    className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[640px] h-[640px] rounded-full opacity-25 blur-[120px]"
                    style={{
                        background:
                            "conic-gradient(from 90deg at 50% 50%, #25D366, #FFE600, #FF2BD6, #00E5FF, #25D366)",
                    }}
                />
            </div>

            <div className="relative px-6 md:px-16">
                <div className="grid grid-cols-12 gap-6 items-center">
                    <div className="col-span-12 md:col-span-8">
                        <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/50">
                            [ Ch. 05 · c ]
                            <br />
                            Fancy Texters
                        </div>
                        <h2 className="font-display uppercase text-5xl md:text-7xl lg:text-8xl leading-[0.88] tracking-tight mt-4">
                            Are you one of them
                            <br />
                            <span className="rainbow-text">fancy texters?</span>
                        </h2>
                        <p className="font-display text-2xl md:text-3xl leading-tight text-white/90 mt-6 max-w-2xl">
                            PaintKing is too. Don't worry — let's talk paint
                            with Paul.
                        </p>
                        <p className="font-mono text-[13px] leading-relaxed text-white/60 mt-4 max-w-xl">
                            One tap opens WhatsApp with Paul. Send a photo of
                            the room, ask for a ballpark, or just say hi —
                            he'll write back between coats.
                        </p>

                        <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                            <motion.a
                                whileHover={{ scale: 1.03 }}
                                whileTap={{ scale: 0.97 }}
                                href={WA_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="whatsapp-cta"
                                className="inline-flex items-center gap-3 bg-[#25D366] hover:bg-[#20BC59] text-black font-mono text-xs md:text-sm tracking-[0.28em] uppercase px-7 py-5 border border-black/20 transition-colors"
                            >
                                <MessageCircle
                                    className="w-5 h-5"
                                    strokeWidth={2.2}
                                />
                                Text Paul On WhatsApp →
                            </motion.a>
                            <a
                                href="tel:5616984044"
                                data-testid="fancy-phone-cta"
                                className="glass-btn inline-flex items-center gap-3 border border-white/20 hover:border-white px-6 py-5 font-mono text-xs md:text-sm tracking-[0.28em] uppercase transition-colors"
                            >
                                <span className="w-1.5 h-1.5 rounded-full bg-[#00FF85] blink" />
                                Old-school call · 561-698-4044
                            </a>
                        </div>
                    </div>

                    <div className="col-span-12 md:col-span-4">
                        <motion.div
                            initial={{ opacity: 0, y: 30, rotate: -2 }}
                            whileInView={{ opacity: 1, y: 0, rotate: -2 }}
                            viewport={{ once: true, amount: 0.3 }}
                            transition={{
                                duration: 1,
                                ease: [0.19, 1, 0.22, 1],
                            }}
                            className="glass border border-white/20 p-5 md:p-6 max-w-sm"
                        >
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-9 h-9 rounded-full bg-[#25D366] flex items-center justify-center">
                                    <MessageCircle
                                        className="w-4 h-4 text-black"
                                        strokeWidth={2.4}
                                    />
                                </div>
                                <div>
                                    <div className="font-display uppercase text-sm leading-none">
                                        Paul · PaintKing
                                    </div>
                                    <div className="font-mono text-[9px] tracking-[0.28em] uppercase text-[#25D366] mt-1">
                                        online · usually replies within 1 hr
                                    </div>
                                </div>
                            </div>
                            <div className="space-y-2">
                                <div className="bg-white/[0.06] border border-white/10 px-4 py-3 rounded-r-2xl rounded-bl-2xl max-w-[85%]">
                                    <p className="font-mono text-[12px] text-white/85 leading-relaxed">
                                        Hey! Send me a pic of the room and I'll
                                        get you a ballpark today.
                                    </p>
                                </div>
                                <div className="bg-[#25D366]/15 border border-[#25D366]/40 px-4 py-3 rounded-l-2xl rounded-br-2xl max-w-[85%] ml-auto">
                                    <p className="font-mono text-[12px] text-white/85 leading-relaxed">
                                        PaintKing chat incoming
                                    </p>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </div>
        </section>
    );
}

/**
 * Persistent floating WhatsApp button. Bottom-right on every page.
 */
export function WhatsAppFab() {
    return (
        <motion.a
            initial={{ opacity: 0, y: 40, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ delay: 2.5, duration: 0.9, ease: [0.19, 1, 0.22, 1] }}
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.95 }}
            href={WA_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="whatsapp-fab"
            aria-label="Chat with Paul on WhatsApp"
            className="fixed bottom-5 right-5 z-40 group flex items-center gap-3 bg-[#25D366] hover:bg-[#20BC59] text-black px-4 py-3 shadow-[0_10px_30px_rgba(0,0,0,0.45)] font-mono text-[11px] tracking-[0.24em] uppercase border border-black/20"
        >
            <span className="relative flex items-center justify-center">
                <span className="absolute inset-0 rounded-full bg-[#25D366] animate-ping opacity-60" />
                <MessageCircle className="w-5 h-5 relative z-10" strokeWidth={2.4} />
            </span>
            <span className="hidden sm:inline">Chat Paul</span>
        </motion.a>
    );
}
